import { createContext, useContext, useRef } from 'react';

// Mutable refs shared between CursorFollower (writer) and 3D layers
// (readers). Refs do not cause re-renders. Context isolated from
// React 18 strict-mode double-invoke so two mount trees can't share
// one set of refs.

export type Cursor3DValue = {
  cx: React.MutableRefObject<number>;
  cy: React.MutableRefObject<number>;
  active: React.MutableRefObject<boolean>;
};

export const Cursor3DContext = createContext<Cursor3DValue | null>(null);

export function Cursor3DProvider({ children }: { children: React.ReactNode }) {
  const cx = useRef(0);
  const cy = useRef(0);
  const active = useRef(false);

  return (
    <Cursor3DContext.Provider value={{ cx, cy, active }}>
      {children}
    </Cursor3DContext.Provider>
  );
}

export function useCursor3D(): Cursor3DValue | null {
  return useContext(Cursor3DContext);
}

// Standalone helper — used by three callbacks that aren't in a React
// render tree. Falls back to no-op if no Provider is mounted.
let fallback: Cursor3DValue | null = null;

export function bindCursor3DTarget(value: Cursor3DValue | null) {
  fallback = value;
}

export function getCursor3D(): { x: number; y: number; active: boolean } {
  if (fallback) {
    return { x: fallback.cx.current, y: fallback.cy.current, active: fallback.active.current };
  }
  return { x: 0, y: 0, active: false };
}
